import { useEffect, useRef, useState, type ChangeEvent, type FormEvent, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, Image as ImageIcon } from 'lucide-react';
import { POSITIVE_REPLY, SCREENSHOT_RULE, STEPS } from '@/lib/outreach-copy';
import { creatorTypeLabel } from '@/lib/labels';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Combobox } from '@/components/ui/combobox';
import { useApi } from '@/hooks/useApi';
import { useMe } from '@/hooks/useMe';
import { queueActionError, usePromoteProspect } from '@/hooks/useDiscoveryQueue';
import type { DiscoveryProspect, OutreachNiche, OutreachRate } from '@/api/types';

/**
 * Skip (wrong fit): why this lead is wrong for this folder.
 *
 * The note is optional but prompted for, because "this is a gaming page" is
 * what tells an admin a folder is drifting; a bare count never does. The
 * copy states the blast radius plainly: an outreacher should not have to
 * wonder whether they just deleted a teammate's lead.
 */
export function SkipDialog({
  prospect,
  onClose,
  onSubmit,
  pending,
}: {
  prospect: DiscoveryProspect;
  onClose: () => void;
  onSubmit: (note: string | null) => void;
  pending: boolean;
}): JSX.Element {
  const [note, setNote] = useState('');

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Skip @{prospect.targetIdentifier} (wrong fit)</DialogTitle>
          <DialogDescription>
            Hides this creator from <strong>your</strong> list only. Your teammates still see
            them and nothing is deleted. An admin reviews skips and decides whether to remove
            the creator for everyone.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(ev: FormEvent) => {
            ev.preventDefault();
            onSubmit(note.trim() || null);
          }}
          className="space-y-3"
        >
          <div className="grid gap-1.5">
            <Label htmlFor="skip-note" className="text-xs">
              What are they actually? <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="skip-note"
              autoFocus
              maxLength={300}
              placeholder="e.g. gaming page, not movie edits"
              value={note}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setNote(e.target.value)}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? 'Skipping…' : 'Skip for me'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Log reply: the creator replied asking how to join. Creates the invite with
 * the proof screenshot, niches and notes, under the team's rules.
 */
export function LogReplyDialog({
  prospect,
  onClose,
}: {
  prospect: DiscoveryProspect;
  onClose: () => void;
}): JSX.Element {
  const api = useApi();
  const me = useMe();
  // Team rules, read here so every caller (queue tabs, My creators) gets the
  // same dialog without wiring them through.
  const allowedCategories =
    me.data?.role === 'outreacher' ? me.data.team?.allowedCategories ?? [] : [];
  const multipleNichesAllowed =
    me.data?.role === 'outreacher' ? me.data.teamSettings.multipleNichesAllowed : false;
  const screenshotRequired =
    me.data?.role === 'outreacher' ? me.data.teamSettings.screenshotRequired : false;
  const promote = usePromoteProspect();
  const [category, setCategory] = useState(
    prospect.suggestedCategory && allowedCategories.includes(prospect.suggestedCategory)
      ? prospect.suggestedCategory
      : allowedCategories[0] ?? '',
  );
  // `me` may still be loading when the dialog opens from a fresh page.
  useEffect(() => {
    if (!category && allowedCategories.length > 0) {
      setCategory(
        prospect.suggestedCategory && allowedCategories.includes(prospect.suggestedCategory)
          ? prospect.suggestedCategory
          : allowedCategories[0] ?? '',
      );
    }
  }, [category, allowedCategories, prospect.suggestedCategory]);
  const [selectedNiches, setSelectedNiches] = useState<string[]>(prospect.niches ?? []);
  const [notes, setNotes] = useState(prospect.notes ?? '');
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const nichesQuery = useQuery({
    queryKey: ['niches', category],
    queryFn: () =>
      api.get<OutreachNiche[]>(
        '/portal/niches',
        category ? { category } : undefined,
      ),
    enabled: Boolean(category),
  });

  const ratesQuery = useQuery({
    queryKey: ['portal-rates'],
    queryFn: () => api.get<OutreachRate[]>('/portal/rates'),
  });

  const bonusAmounts = new Map<string, number>();
  for (const r of ratesQuery.data ?? []) {
    if (!r.isActive || r.niche === null || r.category !== category) continue;
    const amount = Number.parseFloat(r.amount);
    if (!Number.isFinite(amount) || amount <= 0) continue;
    bonusAmounts.set(r.niche, (bonusAmounts.get(r.niche) ?? 0) + amount);
  }

  const orderedNiches = [...(nichesQuery.data ?? [])].sort((a, b) => {
    const bonusA = bonusAmounts.get(a.code) ?? 0;
    const bonusB = bonusAmounts.get(b.code) ?? 0;
    if (bonusA !== bonusB) return bonusB - bonusA;
    if (a.isFocused !== b.isFocused) return a.isFocused ? -1 : 1;
    if (a.displayOrder !== b.displayOrder) return a.displayOrder - b.displayOrder;
    return a.displayName.localeCompare(b.displayName);
  });

  useEffect(() => {
    const valid = new Set(orderedNiches.map((n) => n.code));
    setSelectedNiches((prev) => prev.filter((c) => valid.has(c)));
  }, [nichesQuery.data]);

  useEffect(() => {
    fileInputRef.current?.focus();
  }, []);

  function onFileChange(ev: ChangeEvent<HTMLInputElement>) {
    setFile(ev.target.files?.[0] ?? null);
  }

  function submit(ev: FormEvent) {
    ev.preventDefault();
    if (!category) return;
    if (screenshotRequired && !file) {
      toast.error(SCREENSHOT_RULE.missingError);
      return;
    }
    promote.mutate(
      {
        publicId: prospect.publicId,
        category,
        niches: selectedNiches.length > 0 ? selectedNiches : undefined,
        notes: notes.trim() || null,
        screenshot: file ?? undefined,
      },
      {
        onSuccess: () => {
          toast.success(`Logged @${prospect.targetIdentifier}'s reply. It's in My creators.`);
          onClose();
        },
      },
    );
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Log @{prospect.targetIdentifier}'s reply</DialogTitle>
          <DialogDescription>{SCREENSHOT_RULE.medium}</DialogDescription>
        </DialogHeader>

        <div className="rounded-md border-2 border-warning/50 bg-warning/[0.08] px-3 py-2.5">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <div className="space-y-0.5">
              <p className="text-sm font-bold text-foreground">{POSITIVE_REPLY.headline}</p>
              <p className="text-xs text-foreground/85">{POSITIVE_REPLY.qualifies}</p>
              <p className="text-xs text-foreground/85">{POSITIVE_REPLY.doesNot}</p>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {allowedCategories.length > 1 && (
            <div className="space-y-1.5">
              <Label htmlFor="category">Creator type</Label>
              <select
                id="category"
                value={category}
                onChange={(ev) => setCategory(ev.target.value)}
                required
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                {allowedCategories.map((c) => (
                  <option key={c} value={c}>
                    {creatorTypeLabel(c)}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>{multipleNichesAllowed ? 'Niches (optional)' : 'Niche (optional)'}</Label>
            {multipleNichesAllowed ? (
              <>
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium text-brand">Highlighted</span> niches earn a
                  bonus for this creator type. Only your{' '}
                  <span className="font-medium text-foreground">first selected</span> niche
                  earns the bonus. Any others are saved for context.
                </p>
                <div className="flex flex-wrap gap-2">
                  {nichesQuery.isLoading ? (
                    Array.from({ length: 4 }).map((_, i) => (
                      <span key={i} className="inline-block h-7 w-16 animate-pulse rounded-full bg-muted" />
                    ))
                  ) : orderedNiches.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No niches configured.</p>
                  ) : (
                    orderedNiches.map((n) => {
                      const index = selectedNiches.indexOf(n.code);
                      const active = index !== -1;
                      const bonus = bonusAmounts.get(n.code) ?? 0;
                      const hasBonus = bonus > 0;
                      const isFirst = index === 0;
                      const earnsBonus = isFirst && hasBonus;
                      const bonusLabel = hasBonus
                        ? `+$${Number.isInteger(bonus) ? bonus.toFixed(0) : bonus.toFixed(2)}`
                        : null;

                      const chipClass = active
                        ? 'border-primary bg-primary text-primary-foreground'
                        : hasBonus
                          ? 'border-brand/50 bg-brand/10 text-foreground hover:bg-brand/20'
                          : 'border-input bg-transparent text-foreground hover:bg-muted';

                      return (
                        <button
                          key={n.id}
                          type="button"
                          onClick={() =>
                            setSelectedNiches((prev) =>
                              active ? prev.filter((c) => c !== n.code) : [...prev, n.code],
                            )
                          }
                          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${chipClass}`}
                        >
                          <span>{n.displayName}</span>
                          {n.isFocused && (
                            <span className={active ? 'text-[10px] text-primary-foreground/90' : 'text-[10px] text-warning'}>★</span>
                          )}
                          {bonusLabel && (
                            <span className={active ? 'rounded-sm bg-background/25 px-1 text-[9px] font-mono font-semibold tracking-wide text-primary-foreground' : 'rounded-sm bg-brand/15 px-1 text-[9px] font-mono font-semibold tracking-wide text-brand'}>
                              {bonusLabel}
                            </span>
                          )}
                          {earnsBonus && (
                            <span className="rounded-sm bg-background/30 px-1 text-[9px] font-semibold uppercase tracking-wider text-primary-foreground">
                              Bonus
                            </span>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              </>
            ) : (
              <Combobox
                value={selectedNiches[0] ?? ''}
                onChange={(v) => setSelectedNiches(v ? [v] : [])}
                options={orderedNiches.map((n) => {
                  const bonus = bonusAmounts.get(n.code) ?? 0;
                  return {
                    value: n.code,
                    label: n.displayName,
                    ...(bonus > 0
                      ? { badge: `+$${Number.isInteger(bonus) ? bonus.toFixed(0) : bonus.toFixed(2)}` }
                      : {}),
                    ...(n.isFocused ? { hint: '★' } : {}),
                  };
                })}
                allowAll
                allLabel="None"
                placeholder="Search niches…"
                loading={nichesQuery.isLoading}
              />
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="screenshot">
              Screenshot of their positive reply
              <span
                className={`ml-2 text-xs ${
                  screenshotRequired ? 'text-destructive' : 'text-muted-foreground'
                }`}
              >
                {SCREENSHOT_RULE.labelSuffix(screenshotRequired)}
              </span>
            </Label>
            <p className="text-xs font-medium text-warning">{SCREENSHOT_RULE.short}</p>
            <div className="flex items-center gap-2">
              <Input
                ref={fileInputRef}
                id="screenshot"
                type="file"
                accept="image/*"
                required={screenshotRequired}
                onChange={onFileChange}
                className="cursor-pointer file:mr-2 file:rounded-md file:border-0 file:bg-muted file:px-2 file:py-1 file:text-xs file:font-medium"
              />
              {file && (
                <span className="flex items-center gap-1 whitespace-nowrap text-xs text-muted-foreground">
                  <ImageIcon className="h-3 w-3" /> {(file.size / 1024).toFixed(0)} KB
                </span>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Input
              id="notes"
              value={notes}
              onChange={(ev) => setNotes(ev.target.value)}
              placeholder="Anything you want saved with this reply"
              maxLength={2000}
            />
          </div>

          {promote.error && (
            <p className="text-sm text-destructive">{queueActionError(promote.error)}</p>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={promote.isPending || !category}>
              {promote.isPending ? 'Saving…' : STEPS.logged.verb}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
